import { useState, type FormEvent } from 'react'
import { useAuth } from '../contexts/AuthContext'
import './Login.css'

interface SetupResponse {
  success: boolean
  message?: string
  user?: {
    id: number
    username: string
    role: string
    must_change_password: boolean
  }
}

interface SetupProps {
  onAlreadySetup: () => void
}

export function Setup({ onAlreadySetup }: SetupProps) {
  const { setUser } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    try {
      const response = await fetch('/api/auth/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ username, password }),
      })
      const data: SetupResponse = await response.json()
      if (response.status === 409) {
        onAlreadySetup()
        return
      }
      if (data.success && data.user) {
        onAlreadySetup()
        setUser({
          id: data.user.id,
          username: data.user.username,
          role: data.user.role,
          must_change_password: data.user.must_change_password,
        })
        return
      }
      setError(data.message || 'Could not create admin')
    } catch {
      setError('Could not connect to server')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card card">
          <h1 className="login-title">Create admin</h1>
          <p className="text-secondary" style={{ textAlign: 'center', marginTop: '-0.5rem' }}>
            This server has no users yet. Create the first administrator.
          </p>

          <form onSubmit={handleSubmit} className="login-form">
            {error && <div className="login-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password">Confirm password</label>
              <input
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="btn btn-primary login-button" disabled={loading}>
              {loading ? 'Creating...' : 'Create admin'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
